import { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Calendar, Plus, Truck, Package, Wrench, Users, FileText,
  ShieldCheck, MoreVertical, Clock, CheckCircle2, XCircle, Pause, Play,
  AlertTriangle, Edit3, X, Send, History as HistoryIcon, MapPin, Phone,
  Mail, Building2, UserCircle, CalendarDays, FolderKanban, RefreshCw,
  Boxes, Zap, RadioTower, Globe, Paperclip, Download, Trash,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, KPI, Button, Spinner, EmptyState, Field, Input, Select, Textarea,
} from "@/components/ui";
import { format, parseISO, formatDistanceToNow, isToday, isTomorrow, addDays, startOfDay } from "date-fns";
import CalendarMonth from "./CalendarMonth";
import SubbieComplianceWarning from "@/features/suppliers/SubbieComplianceWarning";
import {
  uploadBookingAttachment, listBookingAttachments, bookingAttachmentUrl,
  removeBookingAttachment, ATTACHMENT_ACCEPT, type BookingFile,
} from "./bookingAttachments";

// =============================================================================
// ProjectScheduleScreen — /projects/:id/schedule
// T-073 — Project bookings (plant / deliveries / integration / etc.).
//
// List view grouped by date. Filter by type + status. Manager+ can create,
// edit, reschedule, mark-in-progress / completed / delayed / cancelled.
// Every shift logs to booking_history with reason.
//
// Calendar view + cross-project view land with T-065 (separate ticket).
// =============================================================================

type BookingType = "plant" | "materials" | "delivery" | "electrical" | "integration" | "subcontractor" | "meeting" | "inspection" | "outage" | "other";
type BookingStatus = "tentative" | "confirmed" | "in_progress" | "completed" | "delayed" | "cancelled";

interface Booking {
  booking_id:             string;
  project_id:             string;
  booking_type:           BookingType;
  title:                  string;
  description:            string | null;
  scheduled_start:        string;
  scheduled_end:          string | null;
  actual_start:           string | null;
  actual_end:             string | null;
  status:                 BookingStatus;
  supplier_id:            string | null;
  contact_employee_id:    string | null;
  external_contact_name:  string | null;
  external_contact_phone: string | null;
  external_contact_email: string | null;
  location_notes:         string | null;
  notes:                  string | null;
  created_at:             string;
  // Migration 325. Outages were going in as type 'other' with the word "Outage"
  // typed in the title, so they could not be filtered, counted or coloured, and
  // the CRQ lived in a note or nowhere. James: "we need better outage / CRQ
  // inputs for our customer and non customer impacting outages the techs log
  // into on site with."
  crq_number:             string | null;
  customer_impacting:     boolean | null;
  outage_approved:        boolean;
  // Promote this one booking onto the company-wide schedule. James: "some key
  // things we may decide we want to see in the overall schedule as they are key
  // dates, outages, maybe big crane lifts."
  show_on_schedule:       boolean;
  supplier?:              { supplier_id: string; legal_name: string; trading_name: string | null } | null;
  contact_employee?:      { employee_id: string; first_name: string; last_name: string } | null;
}

interface BookingHistory {
  history_id:        string;
  booking_id:        string;
  changed_at:        string;
  changed_by_user_id: string | null;
  change_type:       "created" | "rescheduled" | "status_changed" | "edited" | "cancelled" | "reinstated" | "deleted";
  previous_value:    any;
  new_value:         any;
  reason:            string | null;
}

interface ProjectMini {
  project_id:           string;
  project_code:         string;
  project_name:         string;
  scheduled_start_date: string | null;
  scheduled_end_date:   string | null;
  actual_start_date:    string | null;
  actual_end_date:      string | null;
}

const TYPE_META: Record<BookingType, { label: string; icon: any; tone: string }> = {
  plant:         { label: "Plant",         icon: Truck,       tone: "text-blue-400" },
  materials:     { label: "Materials",     icon: Boxes,       tone: "text-emerald-400" },
  delivery:      { label: "Delivery",      icon: Package,     tone: "text-amber-400" },
  electrical:    { label: "Electrical",    icon: Zap,         tone: "text-yellow-400" },
  integration:   { label: "Integration",   icon: ShieldCheck, tone: "text-purple-400" },
  subcontractor: { label: "Subcontractor", icon: Users,       tone: "text-pink-400" },
  meeting:       { label: "Meeting",       icon: Calendar,    tone: "text-text-muted" },
  inspection:    { label: "Inspection",    icon: FileText,    tone: "text-text-muted" },
  outage:        { label: "Outage",        icon: RadioTower,  tone: "text-orange-400" },
  other:         { label: "Other",         icon: Wrench,      tone: "text-text-muted" },
};

const STATUS_TONE: Record<BookingStatus, "open" | "pending" | "approved" | "rejected" | "warning" | "default"> = {
  tentative:    "default",
  confirmed:    "open",
  in_progress:  "pending",
  completed:    "approved",
  delayed:      "warning",
  cancelled:    "rejected",
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  tentative: "tentative", confirmed: "confirmed", in_progress: "in progress",
  completed: "completed", delayed: "delayed", cancelled: "cancelled",
};

// -----------------------------------------------------------------------------

export default function ProjectScheduleScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canManage = hasRole(user, "manager");

  const [filterType,   setFilterType]   = useState<"all" | BookingType>("all");
  const [filterStatus, setFilterStatus] = useState<"upcoming" | "all" | BookingStatus>("upcoming");
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [viewMode,     setViewMode]     = useState<"list" | "calendar">("list");

  // -- Project --
  const { data: project } = useQuery({
    queryKey: ["sched_project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, scheduled_start_date, scheduled_end_date, actual_start_date, actual_end_date")
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectMini | null;
    },
  });

  // -- Bookings --
  const { data: bookings = [], refetch: refetchBookings } = useQuery({
    queryKey: ["sched_bookings", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_bookings")
        .select(`booking_id, project_id, booking_type, title, description,
                 scheduled_start, scheduled_end, actual_start, actual_end, status,
                 supplier_id, contact_employee_id, external_contact_name,
                 external_contact_phone, external_contact_email,
                 location_notes, notes, created_at,
                 crq_number, customer_impacting, outage_approved, show_on_schedule,
                 supplier:supplier_id(supplier_id, legal_name, trading_name),
                 contact_employee:contact_employee_id(employee_id, first_name, last_name)`)
        .eq("project_id", projectId!)
        .order("scheduled_start", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Booking[];
    },
  });

  // -- Project milestones (T-079) — render alongside bookings on the calendar.
  const { data: milestones = [], refetch: refetchMilestones, isFetching: milestonesFetching } = useQuery({
    queryKey: ["project_milestones_for_calendar", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_milestones")
        .select("milestone_id, milestone_name, milestone_date")
        .eq("project_id", projectId!)
        .order("milestone_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        milestone_id: string; milestone_name: string; milestone_date: string;
      }>;
    },
  });

  // Refresh button — pulls latest bookings + milestones in one click. Useful
  // when dates are added/edited elsewhere (e.g. milestone added on Project
  // Detail Overview while this tab is open).
  const [refreshing, setRefreshing] = useState(false);
  async function handleRefresh() {
    setRefreshing(true);
    try {
      await Promise.all([refetchBookings(), refetchMilestones()]);
    } finally {
      setRefreshing(false);
    }
  }

  // -- KPIs --
  const kpis = useMemo(() => {
    const today    = startOfDay(new Date()).getTime();
    const tomorrow = startOfDay(addDays(new Date(), 1)).getTime();
    const weekEnd  = startOfDay(addDays(new Date(), 7)).getTime();
    const monthAgo = startOfDay(addDays(new Date(), -30)).getTime();
    let todayCount = 0, weekCount = 0, delayed = 0, completed30 = 0;
    for (const b of bookings) {
      const start = new Date(b.scheduled_start).getTime();
      if (b.status !== "completed" && b.status !== "cancelled") {
        if (start >= today && start < tomorrow) todayCount++;
        if (start >= today && start < weekEnd)  weekCount++;
        if (b.status === "delayed") delayed++;
      }
      if (b.status === "completed" && b.actual_end && new Date(b.actual_end).getTime() >= monthAgo) {
        completed30++;
      }
    }
    return { todayCount, weekCount, delayed, completed30 };
  }, [bookings]);

  // -- Filter --
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (filterType !== "all"   && b.booking_type !== filterType) return false;
      if (filterStatus === "upcoming") {
        return b.status !== "completed" && b.status !== "cancelled";
      }
      if (filterStatus === "all") return true;
      return b.status === filterStatus;
    });
  }, [bookings, filterType, filterStatus]);

  // Group by date for the list view
  const bookingsByDate = useMemo(() => {
    const m = new Map<string, Booking[]>();
    for (const b of filteredBookings) {
      const day = format(parseISO(b.scheduled_start), "yyyy-MM-dd");
      const arr = m.get(day) ?? [];
      arr.push(b);
      m.set(day, arr);
    }
    return Array.from(m.entries()).sort(([a], [z]) => a.localeCompare(z));
  }, [filteredBookings]);

  if (!user) return null;
  if (!project) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Project not found"
            description="This project doesn't exist or you don't have access to it."
            icon={FolderKanban}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3">
        <ArrowLeft size={14}/> Back to project
      </Link>

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-text-muted">
            {project.project_code} · {project.project_name}
          </div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2 mt-0.5">
            <Calendar size={20} className="text-accent"/>
            Schedule
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Plant arrivals, deliveries, integration days, subbie crews, meetings, inspections.
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            size="md"
            icon={Plus}
            onClick={() => setShowNewModal(true)}
          >
            New booking
          </Button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Today"          value={kpis.todayCount}    icon={Clock}        accent={kpis.todayCount > 0} />
        <KPI label="Next 7 days"    value={kpis.weekCount}     icon={CalendarDays} />
        <KPI label="Delayed"        value={kpis.delayed}       icon={AlertTriangle} accent={kpis.delayed > 0} />
        <KPI label="Completed"      value={kpis.completed30}   icon={CheckCircle2} sub="last 30 days" />
      </div>

      {/* Filter bar + view toggle */}
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* List-only filters (calendar shows everything in scope) */}
          {viewMode === "list" && (
            <>
              <FilterChip label="Upcoming"  active={filterStatus === "upcoming"} onClick={() => setFilterStatus("upcoming")} />
              <FilterChip label="All"       active={filterStatus === "all"}      onClick={() => setFilterStatus("all")} />
              <FilterChip label="Completed" active={filterStatus === "completed"} onClick={() => setFilterStatus("completed")} />
              <span className="mx-1 text-text-dim">·</span>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="text-[12px] py-1"
              >
                <option value="all">All types</option>
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Select>
            </>
          )}
        </div>

        {/* View toggle + refresh */}
        <div className="flex items-center gap-2">
          {/* Refresh — pulls latest bookings + milestones. Spins icon while
              fetching so it's clear something happened. */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || milestonesFetching}
            className="p-2 rounded-md border border-line text-text-muted hover:text-accent hover:border-accent transition-colors disabled:opacity-60"
            title="Refresh — pull latest bookings and milestones"
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={refreshing || milestonesFetching ? "animate-spin" : ""}/>
          </button>

          <div className="flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-3 py-1.5 rounded text-[12px] transition-colors inline-flex items-center gap-1.5 ${
                viewMode === "list"
                  ? "bg-accent/15 text-accent border border-accent/30"
                  : "text-text-muted hover:text-text border border-transparent"
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode("calendar")}
              className={`px-3 py-1.5 rounded text-[12px] transition-colors inline-flex items-center gap-1.5 ${
                viewMode === "calendar"
                  ? "bg-accent/15 text-accent border border-accent/30"
                  : "text-text-muted hover:text-text border border-transparent"
              }`}
            >
              Calendar
            </button>
          </div>
        </div>
      </div>

      {/* Bookings — list OR calendar
          Calendar view always renders, even with zero bookings, so the
          project's scheduled_start / scheduled_end and milestones still
          surface. Not every project has bookings, but every project has
          dates worth seeing on a calendar. (James 2026-04-29)
          List view still falls back to the empty state when there's
          nothing to list — a list of nothing is just blank space. */}
      {viewMode === "calendar" ? (
        <Card>
          <div className="p-4">
            <CalendarMonth
              bookings={bookings}
              milestones={milestones}
              projectDates={project ? {
                scheduledStart: project.scheduled_start_date,
                scheduledEnd:   project.scheduled_end_date,
                actualStart:    project.actual_start_date,
                actualEnd:      project.actual_end_date,
              } : undefined}
              onBookingClick={(id) => setEditingId(id)}
            />
          </div>
          {bookings.length === 0 && (
            <div className="px-5 pb-4 text-[11px] text-text-muted italic">
              No bookings yet — calendar shows the project's scheduled dates and milestones only.
              {canManage && " Use “New booking” (top right) to add one."}
            </div>
          )}
        </Card>
      ) : bookings.length === 0 ? (
        <Card>
          <EmptyState
            title="No bookings yet"
            description={canManage
              ? "Create the first booking — plant arrival, delivery, carrier integration day, or anything else with a date and a contact. Or switch to Calendar view to see project dates + milestones."
              : "Your manager hasn't created any bookings for this project yet. Switch to Calendar view to see project dates + milestones."}
            icon={Calendar}
          />
        </Card>
      ) : filteredBookings.length === 0 ? (
        <Card>
          <EmptyState
            title="Nothing matches"
            description="Try a different status or type filter."
            icon={Calendar}
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {bookingsByDate.map(([day, dayBookings]) => (
            <DateSection
              key={day}
              dayKey={day}
              bookings={dayBookings}
              canManage={canManage}
              onEdit={(id) => setEditingId(id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showNewModal && (
        <BookingFormModal
          projectId={projectId!}
          onClose={() => setShowNewModal(false)}
          onSaved={() => {
            setShowNewModal(false);
            refetchBookings();
          }}
        />
      )}
      {editingId && (
        <BookingDetailModal
          bookingId={editingId}
          canManage={canManage}
          onClose={() => setEditingId(null)}
          onChanged={() => {
            refetchBookings();
            qc.invalidateQueries({ queryKey: ["sched_booking_detail", editingId] });
            qc.invalidateQueries({ queryKey: ["sched_booking_history", editingId] });
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded text-[11px] transition-colors whitespace-nowrap border ${
        active
          ? "bg-accent/15 text-accent border-accent/30"
          : "text-text-muted border-line hover:border-text-muted"
      }`}
    >
      {label}
    </button>
  );
}

// -----------------------------------------------------------------------------
function DateSection({
  dayKey, bookings, canManage, onEdit,
}: {
  dayKey: string; bookings: Booking[]; canManage: boolean; onEdit: (id: string) => void;
}) {
  const day = parseISO(dayKey + "T00:00:00");
  const dayLabel =
    isToday(day)    ? "Today"
    : isTomorrow(day) ? "Tomorrow"
    : format(day, "EEEE dd MMM yyyy");

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <div className="h-1 w-8 bg-accent rounded-full"/>
        <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
          {dayLabel}
        </div>
      </div>
      <div className="space-y-2">
        {bookings.map(b => <BookingCard key={b.booking_id} booking={b} canManage={canManage} onEdit={() => onEdit(b.booking_id)} />)}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function BookingCard({ booking, canManage, onEdit }: { booking: Booking; canManage: boolean; onEdit: () => void }) {
  const meta = TYPE_META[booking.booking_type];
  const Icon = meta.icon;
  const start = parseISO(booking.scheduled_start);
  const end   = booking.scheduled_end ? parseISO(booking.scheduled_end) : null;

  const contactLabel =
    booking.contact_employee
      ? `${booking.contact_employee.first_name} ${booking.contact_employee.last_name}`
      : booking.supplier
        ? (booking.supplier.trading_name ?? booking.supplier.legal_name)
        : booking.external_contact_name
          ? booking.external_contact_name
          : null;

  return (
    <Card className="hover:border-accent transition-colors group cursor-pointer" onClick={onEdit}>
      <div className="p-3 md:p-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded bg-ink-raised border border-line flex items-center justify-center flex-shrink-0 ${meta.tone}`}>
          <Icon size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-text font-medium">{booking.title}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">{meta.label}</span>
              </div>
              <div className="text-[11px] text-text-muted mt-0.5 inline-flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Clock size={10}/>
                  {format(start, "HH:mm")}
                  {end && ` – ${format(end, "HH:mm")}`}
                </span>
                {contactLabel && (
                  <span className="inline-flex items-center gap-1">
                    <UserCircle size={10}/> {contactLabel}
                  </span>
                )}
                {booking.location_notes && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={10}/> {booking.location_notes}
                  </span>
                )}
                {booking.crq_number && (
                  <span className="inline-flex items-center gap-1 font-mono text-orange-300">
                    <RadioTower size={10}/> {booking.crq_number}
                  </span>
                )}
                {booking.show_on_schedule && (
                  <span className="inline-flex items-center gap-1 text-text-dim"
                    title="Also showing on the company-wide schedule">
                    <Globe size={10}/> on company schedule
                  </span>
                )}
              </div>
              {/* Service affecting is the fact that changes what has to happen
                  before the window, so it gets a word and an icon, not a colour
                  on its own. */}
              {booking.booking_type === "outage" && (
                <div className="text-[11px] mt-1 inline-flex items-center gap-3 flex-wrap">
                  {booking.customer_impacting === true && (
                    <span className="inline-flex items-center gap-1 text-warn">
                      <AlertTriangle size={10}/> Service affecting
                    </span>
                  )}
                  {booking.customer_impacting === false && (
                    <span className="inline-flex items-center gap-1 text-text-muted">
                      <CheckCircle2 size={10}/> Non service affecting
                    </span>
                  )}
                  {booking.customer_impacting == null && (
                    <span className="inline-flex items-center gap-1 text-text-dim">
                      <AlertTriangle size={10}/> Impact not confirmed
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 ${booking.outage_approved ? "text-ok" : "text-text-dim"}`}>
                    {booking.outage_approved
                      ? <><CheckCircle2 size={10}/> Carrier approved</>
                      : <><Clock size={10}/> Awaiting carrier approval</>}
                  </span>
                </div>
              )}
              {booking.description && (
                <p className="text-[12px] text-text-muted mt-1 line-clamp-2">{booking.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Pill label={STATUS_LABEL[booking.status]} tone={STATUS_TONE[booking.status]} small />
              {canManage && (
                <Edit3 size={13} className="text-text-dim group-hover:text-accent transition-colors"/>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Booking form modal (NEW + EDIT share the form, EDIT has the detail panel)
// -----------------------------------------------------------------------------
interface FormState {
  booking_type:           BookingType;
  title:                  string;
  description:            string;
  scheduled_start:        string;   // datetime-local format
  scheduled_end:          string;
  status:                 BookingStatus;
  supplier_id:            string;
  contact_employee_id:    string;
  external_contact_name:  string;
  external_contact_phone: string;
  external_contact_email: string;
  location_notes:         string;
  notes:                  string;
  crq_number:             string;
  // Tri-state on purpose. "" is "not answered yet", which is different from
  // "no, it does not drop service" - and on an outage the difference decides
  // who has to be told and how far ahead.
  customer_impacting:     "" | "yes" | "no";
  outage_approved:        boolean;
  show_on_schedule:       boolean;
}

const EMPTY_FORM: FormState = {
  booking_type: "plant",
  title: "",
  description: "",
  scheduled_start: "",
  scheduled_end: "",
  // Default to confirmed — most bookings are locked in when created.
  // Users can flip to tentative if they're still working out details.
  status: "confirmed",
  supplier_id: "",
  contact_employee_id: "",
  external_contact_name: "",
  external_contact_phone: "",
  external_contact_email: "",
  location_notes: "",
  notes: "",
  crq_number: "",
  customer_impacting: "",
  outage_approved: false,
  show_on_schedule: false,
};

function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  // datetime-local needs YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local: string): string | null {
  if (!local) return null;
  return new Date(local).toISOString();
}

// -----------------------------------------------------------------------------
// AttachmentList — pick, list, open, remove. Shared by the pending (pre-create)
// and saved cases so a file looks the same before and after the booking exists.
// -----------------------------------------------------------------------------
function AttachmentList({
  pending, existing, busy, onPick, onDropPending, onOpen, onDetach,
}: {
  pending: File[];
  existing: BookingFile[];
  busy: boolean;
  onPick: (files: FileList | null) => void;
  onDropPending: (index: number) => void;
  onOpen: (f: BookingFile) => void;
  onDetach: (f: BookingFile) => void;
}) {
  const kb = (n: number | null) =>
    n == null ? "" : n < 1024 * 1024
      ? `${Math.max(1, Math.round(n / 1024))} KB`
      : `${(n / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div className="space-y-1.5">
      <label className={`flex items-center justify-center gap-2 rounded border border-dashed border-line
                         px-3 py-2 text-[12px] transition-colors
                         ${busy ? "opacity-60" : "cursor-pointer hover:border-accent hover:text-accent text-text-muted"}`}>
        <input
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          disabled={busy}
          className="hidden"
          onChange={e => { onPick(e.target.files); e.currentTarget.value = ""; }}
        />
        {busy ? <Spinner size={12}/> : <Paperclip size={13}/>}
        {busy ? "Uploading…" : "Attach a file"}
      </label>

      {pending.map((f, i) => (
        <div key={`p-${i}`}
          className="flex items-center gap-2 text-[12px] rounded border border-line px-2 py-1.5 bg-ink">
          <Paperclip size={12} className="text-text-muted flex-shrink-0"/>
          <span className="truncate flex-1">{f.name}</span>
          <span className="text-[10px] text-text-dim flex-shrink-0">{kb(f.size)}</span>
          {/* Says what it is, because "attached" and "attached once you save"
              are different states and only one survives hitting Cancel. */}
          <span className="text-[10px] text-text-dim flex-shrink-0">on save</span>
          <button type="button" onClick={() => onDropPending(i)}
            className="text-text-dim hover:text-danger flex-shrink-0" title="Remove">
            <X size={12}/>
          </button>
        </div>
      ))}

      {existing.map(f => (
        <div key={f.file_id}
          className="flex items-center gap-2 text-[12px] rounded border border-line px-2 py-1.5 bg-ink">
          <Paperclip size={12} className="text-text-muted flex-shrink-0"/>
          <button type="button" onClick={() => onOpen(f)}
            className="truncate flex-1 text-left hover:text-accent hover:underline">
            {f.file_name}
          </button>
          <span className="text-[10px] text-text-dim flex-shrink-0">{kb(f.file_size_bytes)}</span>
          <button type="button" onClick={() => onOpen(f)}
            className="text-text-dim hover:text-accent flex-shrink-0" title="Open">
            <Download size={12}/>
          </button>
          <button type="button" onClick={() => onDetach(f)}
            className="text-text-dim hover:text-danger flex-shrink-0" title="Remove from this booking">
            <Trash size={12}/>
          </button>
        </div>
      ))}
    </div>
  );
}

function BookingFormModal({
  projectId, onClose, onSaved, initial,
}: {
  projectId: string;
  onClose: () => void;
  onSaved: () => void;
  initial?: Booking;
}) {
  const [form, setForm] = useState<FormState>(() => initial ? {
    booking_type: initial.booking_type,
    title:        initial.title,
    description:  initial.description ?? "",
    scheduled_start: toLocalInputValue(initial.scheduled_start),
    scheduled_end:   toLocalInputValue(initial.scheduled_end),
    status:       initial.status,
    supplier_id:  initial.supplier_id ?? "",
    contact_employee_id: initial.contact_employee_id ?? "",
    external_contact_name: initial.external_contact_name ?? "",
    external_contact_phone: initial.external_contact_phone ?? "",
    external_contact_email: initial.external_contact_email ?? "",
    location_notes: initial.location_notes ?? "",
    notes:          initial.notes ?? "",
    crq_number:     initial.crq_number ?? "",
    customer_impacting: initial.customer_impacting == null
      ? "" : initial.customer_impacting ? "yes" : "no",
    outage_approved:  initial.outage_approved ?? false,
    show_on_schedule: initial.show_on_schedule ?? false,
  } : EMPTY_FORM);

  const [error, setError] = useState<string | null>(null);

  // Files chosen but not yet uploaded. On a NEW booking there is no booking_id
  // to hang them off until the row exists, so they wait here and go up straight
  // after the create. On an edit they upload immediately.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<BookingFile[]>([]);
  const [fileBusy, setFileBusy] = useState(false);

  useEffect(() => {
    if (!initial) return;
    listBookingAttachments(initial.booking_id)
      .then(setExistingFiles)
      .catch(() => { /* an attachment list that will not load must not block the form */ });
  }, [initial?.booking_id]);

  async function attachNow(files: FileList | null) {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    if (!initial) { setPendingFiles(prev => [...prev, ...list]); return; }
    setFileBusy(true); setError(null);
    try {
      for (const f of list) await uploadBookingAttachment(initial.booking_id, f);
      setExistingFiles(await listBookingAttachments(initial.booking_id));
    } catch (e: any) {
      setError(e?.message ?? "Attachment failed");
    } finally {
      setFileBusy(false);
    }
  }

  async function openAttachment(f: BookingFile) {
    try {
      const url = await bookingAttachmentUrl(f.r2_object_key);
      window.open(url, "_blank", "noopener");
    } catch (e: any) { setError(e?.message ?? "Could not open that file"); }
  }

  async function detach(f: BookingFile) {
    if (!window.confirm(`Remove ${f.file_name} from this booking?`)) return;
    try {
      await removeBookingAttachment(f.file_id);
      setExistingFiles(prev => prev.filter(x => x.file_id !== f.file_id));
    } catch (e: any) { setError(e?.message ?? "Could not remove that file"); }
  }

  // Suppliers + employees for pickers (lazy-loaded)
  const { data: suppliers = [] } = useQuery({
    queryKey: ["sched_suppliers"],
    queryFn: async () => {
      const { data } = await supabase
        .from("suppliers")
        .select("supplier_id, legal_name, trading_name")
        .eq("is_active", true)
        .order("legal_name");
      return data ?? [];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["sched_employees"],
    queryFn: async () => {
      const { data } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name")
        .order("first_name");
      return data ?? [];
    },
  });

  // An outage is not a delivery with a different word on it. Different fields,
  // a different external contact, and a different reason to be on the schedule.
  const isOutage = form.booking_type === "outage";

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form.title.trim()) throw new Error("Title is required");
      if (!form.scheduled_start) throw new Error("Scheduled start is required");
      if (form.scheduled_end && form.scheduled_start &&
          new Date(form.scheduled_end).getTime() < new Date(form.scheduled_start).getTime()) {
        throw new Error("Scheduled end is before the start. End is optional — clear it, or pick a date/time after the start.");
      }
      // An outage without a CRQ is an outage the tech cannot log on for. This is
      // the one field worth refusing to save without, because it is the number
      // they quote on the phone to the NIC and it cannot be worked out later.
      if (isOutage && !form.crq_number.trim()) {
        throw new Error("An outage needs its CRQ number — it is what the tech quotes to the NIC to log on.");
      }

      // Only ever send the outage fields on an outage. Leaving a CRQ behind on a
      // booking that got switched back to a delivery would be a lie that survives
      // in the record.
      const outageFields = {
        crq_number:         isOutage ? (form.crq_number.trim() || null) : null,
        customer_impacting: isOutage && form.customer_impacting !== ""
          ? form.customer_impacting === "yes" : null,
        outage_approved:    isOutage ? form.outage_approved : false,
      };

      if (initial) {
        // UPDATE direct (no RPC for plain edits — RLS gates mgr+)
        const { error } = await supabase
          .from("project_bookings")
          .update({
            booking_type:        form.booking_type,
            title:               form.title,
            description:         form.description || null,
            scheduled_start:     fromLocalInputValue(form.scheduled_start),
            scheduled_end:       fromLocalInputValue(form.scheduled_end),
            status:              form.status,
            supplier_id:         isOutage ? null : (form.supplier_id || null),
            contact_employee_id: form.contact_employee_id || null,
            external_contact_name:  form.external_contact_name  || null,
            external_contact_phone: form.external_contact_phone || null,
            external_contact_email: form.external_contact_email || null,
            location_notes:      form.location_notes || null,
            notes:               form.notes || null,
            ...outageFields,
            show_on_schedule:    form.show_on_schedule,
          })
          .eq("booking_id", initial.booking_id);
        if (error) throw error;
        // Best-effort: log an 'edited' history row (could go through an RPC later)
        await supabase.from("booking_history").insert({
          booking_id:      initial.booking_id,
          change_type:     "edited",
          new_value:       { title: form.title, status: form.status },
        });
      } else {
        // CREATE via RPC for the history insert in same txn
        const { data, error } = await supabase.schema("app").rpc("create_project_booking", {
          p_project_id:          projectId,
          p_booking_type:        form.booking_type,
          p_title:               form.title,
          p_description:         form.description || null,
          p_scheduled_start:     fromLocalInputValue(form.scheduled_start),
          p_scheduled_end:       fromLocalInputValue(form.scheduled_end),
          p_supplier_id:         isOutage ? null : (form.supplier_id || null),
          p_contact_employee_id: form.contact_employee_id || null,
          p_external_contact_name:  form.external_contact_name  || null,
          p_external_contact_phone: form.external_contact_phone || null,
          p_external_contact_email: form.external_contact_email || null,
          p_location_notes:      form.location_notes || null,
          p_notes:               form.notes || null,
        });
        if (error) throw error;

        // app.create_project_booking predates these columns and takes all 13 of
        // its parameters positionally with no defaults, so adding more would
        // mean either an overloaded signature (ambiguous when called by name) or
        // dropping and recreating a SECURITY DEFINER function and its grants.
        // It already RETURNS the new booking_id, so the honest, low-risk move is
        // a second statement.
        const newId = data as unknown as string | null;

        // ---- STATUS. This is a bug fix, not a new feature. -----------------
        // The RPC's insert column list has never included status, so every
        // booking ever created through this form has landed on the column
        // default - tentative - no matter what the Status dropdown said. James
        // picked "confirmed" on an outage and got a booking marked TENTATIVE,
        // which is how it surfaced. The dropdown has been decorative on create
        // the whole time; it only ever worked on edit.
        // Typed against the columns, not Record<string, unknown> - the newer
        // supabase-js rejects an untyped bag against the generated Update type
        // (RejectExcessProperties), and it is right to: a typo'd column name in
        // a loose record would vanish silently instead of failing the build.
        const patch: {
          crq_number: string | null;
          customer_impacting: boolean | null;
          outage_approved: boolean;
          show_on_schedule: boolean;
          status?: BookingStatus;
        } = {
          ...outageFields,
          show_on_schedule: form.show_on_schedule,
        };
        if (form.status !== "tentative") patch.status = form.status;

        const needsPatch = patch.status != null
          || outageFields.crq_number != null
          || outageFields.customer_impacting != null
          || outageFields.outage_approved
          || form.show_on_schedule;

        if (needsPatch) {
          if (!newId) {
            throw new Error("Booking was created, but its details could not be set — open it and check them.");
          }
          const { error: e2 } = await supabase
            .from("project_bookings").update(patch).eq("booking_id", newId);
          if (e2) throw new Error(`Booking created, but its details did not save: ${e2.message}`);
        }

        // Attachments need a booking_id, so they go up after the row exists.
        // Deliberately last and deliberately non-fatal: the booking is the thing
        // that matters, and a failed upload must not lose it.
        if (pendingFiles.length && newId) {
          const failed: string[] = [];
          for (const f of pendingFiles) {
            try { await uploadBookingAttachment(newId, f); }
            catch (e: any) { failed.push(`${f.name}: ${e?.message ?? e}`); }
          }
          if (failed.length) {
            throw new Error(`Booking saved, but some attachments did not: ${failed.join("; ")}`);
          }
        }
      }
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(e?.message ?? "Save failed"),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[16px] font-semibold flex items-center gap-2">
            <Calendar size={16} className="text-accent"/>
            {initial ? "Edit booking" : "New booking"}
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button">
            <X size={16}/>
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Type">
            <Select value={form.booking_type}
              onChange={(e) => {
                const t = e.target.value as BookingType;
                setForm({ ...form, booking_type: t, supplier_id: t === "outage" ? "" : form.supplier_id });
              }}>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Crane arrival — Brisbane Valley site" />
          </Field>
          <Field label="Description (optional)">
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Scheduled start">
              <Input type="datetime-local" value={form.scheduled_start} onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })} />
            </Field>
            <Field label="Scheduled end (optional)">
              <Input type="datetime-local" min={form.scheduled_start || undefined} value={form.scheduled_end} onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })} />
            </Field>
          </div>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as BookingStatus })}>
              {(Object.keys(STATUS_LABEL) as BookingStatus[]).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </Select>
          </Field>
          {/* James: "if it's an outage we won't need a supplier." Right - an
              outage is booked with a carrier's NIC, not bought from a supplier,
              and the supplier field on one has never been filled in. Hidden
              rather than disabled, and the value is cleared when the type
              changes, so a supplier can't survive on a booking that has no
              business carrying one. */}
          {!isOutage && (
            <Field label="Supplier (optional)">
              <Select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                <option value="">— none —</option>
                {suppliers.map((s: any) => <option key={s.supplier_id} value={s.supplier_id}>{s.trading_name ?? s.legal_name}</option>)}
              </Select>
              {/* T-319 — warn (don't block) if this supplier is a non-compliant subbie */}
              <SubbieComplianceWarning supplierId={form.supplier_id || null} className="mt-1.5" />
            </Field>
          )}
          <Field label="Internal contact (optional)">
            <Select value={form.contact_employee_id} onChange={(e) => setForm({ ...form, contact_employee_id: e.target.value })}>
              <option value="">— none —</option>
              {employees.map((e: any) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
            </Select>
          </Field>
          {/* ---- outage / CRQ -------------------------------------------
              Only on an outage, because on a plant delivery these are four
              fields of noise. James: "we need better outage / CRQ inputs for
              our customer and non customer impacting outages the techs log
              into on site with for that activity."                      */}
          {isOutage && (
            <div className="rounded border border-orange-400/30 bg-orange-400/[0.04] p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-wider2 text-orange-300/90 font-mono flex items-center gap-1.5">
                <RadioTower size={11}/> Outage details
              </div>
              <Field label="CRQ number" hint="The carrier's change reference — the number the tech quotes when they ring the NIC to log on.">
                <Input
                  mono
                  value={form.crq_number}
                  onChange={(e) => setForm({ ...form, crq_number: e.target.value })}
                  placeholder="e.g. CRQ000000123456"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Customer impacting?" hint="Does this drop customer service? Changes who has to be told, and how far ahead.">
                  <Select
                    value={form.customer_impacting}
                    onChange={(e) => setForm({ ...form, customer_impacting: e.target.value as FormState["customer_impacting"] })}
                  >
                    <option value="">— not confirmed yet —</option>
                    <option value="yes">Yes — service affecting</option>
                    <option value="no">No — non service affecting</option>
                  </Select>
                </Field>
                <Field label="Carrier approval" hint="A window can be confirmed with us and still not approved by them.">
                  <div className="flex items-center h-[42px]">
                    <label className="inline-flex items-center gap-2 text-[13px]">
                      <input
                        type="checkbox"
                        checked={form.outage_approved}
                        onChange={(e) => setForm({ ...form, outage_approved: e.target.checked })}
                      />
                      Carrier has approved the window
                    </label>
                  </div>
                </Field>
              </div>
              {form.customer_impacting === "yes" && (
                <div className="text-[11px] text-warn flex items-start gap-1.5">
                  <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/>
                  <span>Service affecting — customer notification lead time applies.</span>
                </div>
              )}

              {/* ---- the approval itself ------------------------------------
                  James: "maybe we have an attachment option to attach the CRQ
                  approval email from the carrier / client — sometimes it's a
                  snip." A tick saying the carrier approved it is a claim; the
                  email is the evidence, and the evidence is what gets asked for
                  at 2am or in an incident review. Inside the outage box because
                  that is where the claim it backs up lives. */}
              <div>
                <div className="text-[11px] text-text-muted mb-1.5">
                  Carrier approval — email, PDF or a screenshot
                </div>
                <AttachmentList
                  pending={pendingFiles}
                  existing={existingFiles}
                  busy={fileBusy}
                  onPick={attachNow}
                  onDropPending={i => setPendingFiles(prev => prev.filter((_, x) => x !== i))}
                  onOpen={openAttachment}
                  onDetach={detach}
                />
              </div>
            </div>
          )}

          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono pt-2">
            {isOutage ? "Carrier / NIC engineer" : "External contact (optional)"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input value={form.external_contact_name}  onChange={(e) => setForm({ ...form, external_contact_name:  e.target.value })} placeholder={isOutage ? "Engineer name" : "Name"} />
            <Input value={form.external_contact_phone} onChange={(e) => setForm({ ...form, external_contact_phone: e.target.value })} placeholder="Phone" />
            <Input value={form.external_contact_email} onChange={(e) => setForm({ ...form, external_contact_email: e.target.value })} placeholder="Email" />
          </div>
          <Field label="Location notes (optional)">
            <Input value={form.location_notes} onChange={(e) => setForm({ ...form, location_notes: e.target.value })} placeholder="south gate / via Smith St entrance" />
          </Field>
          <Field label="Notes (optional)">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </Field>

          {/* ---- promote to the company schedule ------------------------
              James: "I would like to be able to flag things in a project's
              calendar / bookings / outages — a option to tick in the project
              to show on this calendar, then it's in the build / site bar but
              maybe a different colour to highlight it, and if you click it
              you see the details."

              Off by default and deliberately so: a company schedule showing
              every plant delivery on every job is a company schedule nobody
              reads.                                                    */}
          <label className="flex items-start gap-2.5 rounded border border-line p-3 cursor-pointer hover:border-line/80 transition-colors">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.show_on_schedule}
              onChange={(e) => setForm({ ...form, show_on_schedule: e.target.checked })}
            />
            <span className="min-w-0">
              <span className="text-[13px] flex items-center gap-1.5">
                <Globe size={12} className="text-text-muted"/> Show on the company schedule
              </span>
              <span className="block text-[11px] text-text-muted mt-0.5">
                Puts this one date on the crew schedule everyone sees, as well as
                this project's own calendar. For the handful that everybody needs
                to know about — an outage window, a big crane lift.
              </span>
            </span>
          </label>
        </div>

        {error && (
          <div className="mt-3 text-[12px] text-danger flex items-start gap-1.5">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/>
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" icon={Send} onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : initial ? "Save changes" : "Create booking"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Detail modal — shows booking, status actions, reschedule, history
// -----------------------------------------------------------------------------
function BookingDetailModal({
  bookingId, canManage, onClose, onChanged,
}: {
  bookingId: string;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [showEdit, setShowEdit]     = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [statusAction, setStatusAction]     = useState<BookingStatus | null>(null);

  const { data: booking } = useQuery({
    queryKey: ["sched_booking_detail", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_bookings")
        .select(`booking_id, project_id, booking_type, title, description,
                 scheduled_start, scheduled_end, actual_start, actual_end, status,
                 supplier_id, contact_employee_id, external_contact_name,
                 external_contact_phone, external_contact_email,
                 location_notes, notes, created_at,
                 crq_number, customer_impacting, outage_approved, show_on_schedule,
                 supplier:supplier_id(supplier_id, legal_name, trading_name),
                 contact_employee:contact_employee_id(employee_id, first_name, last_name)`)
        .eq("booking_id", bookingId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as Booking | null;
    },
  });

  const { data: history = [] } = useQuery({
    queryKey: ["sched_booking_history", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_history")
        .select("history_id, booking_id, changed_at, changed_by_user_id, change_type, previous_value, new_value, reason")
        .eq("booking_id", bookingId)
        .order("changed_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BookingHistory[];
    },
  });

  if (!booking) return null;
  const meta = TYPE_META[booking.booking_type];

  if (showEdit) {
    return (
      <BookingFormModal
        projectId={booking.project_id}
        initial={booking}
        onClose={() => setShowEdit(false)}
        onSaved={() => {
          setShowEdit(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold flex items-center gap-2 flex-wrap">
              <span className={meta.tone}><meta.icon size={16}/></span>
              {booking.title}
              <Pill label={STATUS_LABEL[booking.status]} tone={STATUS_TONE[booking.status]} small />
            </h3>
            <div className="text-[11px] font-mono text-text-muted mt-1">
              {format(parseISO(booking.scheduled_start), "EEE dd MMM yyyy HH:mm")}
              {booking.scheduled_end && ` – ${format(parseISO(booking.scheduled_end), "HH:mm")}`}
              {" · "}{meta.label}
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text flex-shrink-0" type="button">
            <X size={16}/>
          </button>
        </div>

        {booking.description && (
          <p className="text-[13px] text-text mb-3 whitespace-pre-wrap">{booking.description}</p>
        )}

        {/* Contact + location */}
        <div className="space-y-1.5 mb-3 text-[12px]">
          {booking.contact_employee && (
            <div className="inline-flex items-center gap-2"><UserCircle size={12} className="text-text-dim"/> Internal: <span className="text-text">{booking.contact_employee.first_name} {booking.contact_employee.last_name}</span></div>
          )}
          {booking.supplier && (
            <div className="inline-flex items-center gap-2"><Building2 size={12} className="text-text-dim"/> Supplier: <span className="text-text">{booking.supplier.trading_name ?? booking.supplier.legal_name}</span></div>
          )}
          {booking.external_contact_name && (
            <div className="inline-flex items-center gap-2 flex-wrap">
              <UserCircle size={12} className="text-text-dim"/>
              <span className="text-text">{booking.external_contact_name}</span>
              {booking.external_contact_phone && (<><Phone size={11} className="text-text-dim ml-2"/> <a className="text-accent hover:underline" href={`tel:${booking.external_contact_phone}`}>{booking.external_contact_phone}</a></>)}
              {booking.external_contact_email && (<><Mail size={11} className="text-text-dim ml-2"/> <a className="text-accent hover:underline" href={`mailto:${booking.external_contact_email}`}>{booking.external_contact_email}</a></>)}
            </div>
          )}
          {booking.location_notes && (
            <div className="inline-flex items-center gap-2"><MapPin size={12} className="text-text-dim"/> <span className="text-text">{booking.location_notes}</span></div>
          )}
        </div>

        {booking.notes && (
          <div className="mb-3 p-2.5 rounded bg-ink border border-line text-[12px] text-text whitespace-pre-wrap">
            {booking.notes}
          </div>
        )}

        {/* Manager actions */}
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap mb-4 pt-3 border-t border-line">
            {/* Confirm — only for tentative. Primary action when shown. */}
            {booking.status === "tentative" && (
              <Button variant="primary" size="sm" icon={CheckCircle2} onClick={() => setStatusAction("confirmed")}>
                Confirm booking
              </Button>
            )}
            <Button variant="secondary" size="sm" icon={Edit3} onClick={() => setShowEdit(true)}>Edit</Button>
            <Button variant="secondary" size="sm" icon={Clock} onClick={() => setRescheduleOpen(true)}>Reschedule</Button>
            {booking.status !== "completed" && booking.status !== "cancelled" && (
              <Button variant="secondary" size="sm" icon={CheckCircle2} onClick={() => setStatusAction("completed")}>Mark completed</Button>
            )}
            {booking.status !== "cancelled" && booking.status !== "completed" && (
              <Button variant="secondary" size="sm" icon={XCircle} onClick={() => setStatusAction("cancelled")} className="text-danger hover:border-danger/50">Cancel</Button>
            )}
          </div>
        )}

        {/* History */}
        <div className="mt-4 pt-3 border-t border-line">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2 flex items-center gap-1.5">
            <HistoryIcon size={11}/> History
          </div>
          {history.length === 0 ? (
            <div className="text-[11px] text-text-dim italic">No history yet.</div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.map(h => <HistoryRow key={h.history_id} entry={h}/>)}
            </div>
          )}
        </div>
      </div>

      {rescheduleOpen && (
        <RescheduleModal
          booking={booking}
          onClose={() => setRescheduleOpen(false)}
          onDone={() => { setRescheduleOpen(false); onChanged(); }}
        />
      )}
      {statusAction && (
        <StatusReasonModal
          booking={booking}
          newStatus={statusAction}
          onClose={() => setStatusAction(null)}
          onDone={() => { setStatusAction(null); onChanged(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function HistoryRow({ entry }: { entry: BookingHistory }) {
  const colour =
    entry.change_type === "cancelled" ? "text-danger"
    : entry.change_type === "rescheduled" ? "text-warn"
    : entry.change_type === "status_changed" ? "text-accent"
    : "text-text-muted";
  return (
    <div className="text-[11px] flex items-start gap-2">
      <span className={`font-mono uppercase tracking-wider2 w-24 flex-shrink-0 ${colour}`}>{entry.change_type}</span>
      <div className="flex-1 min-w-0">
        <div className="text-text">
          {entry.reason ?? <span className="italic text-text-dim">no reason given</span>}
        </div>
        <div className="text-[10px] font-mono text-text-dim mt-0.5">
          {formatDistanceToNow(parseISO(entry.changed_at), { addSuffix: true })}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function RescheduleModal({
  booking, onClose, onDone,
}: {
  booking: Booking; onClose: () => void; onDone: () => void;
}) {
  const [newStart, setNewStart] = useState(toLocalInputValue(booking.scheduled_start));
  const [newEnd,   setNewEnd]   = useState(toLocalInputValue(booking.scheduled_end));
  const [reason,   setReason]   = useState("");
  const [setDelayed, setSetDelayed] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!newStart) throw new Error("New start time required");
      const { error } = await supabase.schema("app").rpc("reschedule_project_booking", {
        p_booking_id:         booking.booking_id,
        p_new_start:          fromLocalInputValue(newStart),
        p_new_end:            fromLocalInputValue(newEnd),
        p_reason:             reason || null,
        p_set_status_delayed: setDelayed,
      });
      if (error) throw error;
    },
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? "Reschedule failed"),
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <Clock size={15} className="text-warn"/> Reschedule booking
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="New start"><Input type="datetime-local" value={newStart} onChange={e => setNewStart(e.target.value)}/></Field>
            <Field label="New end"><Input type="datetime-local" value={newEnd} onChange={e => setNewEnd(e.target.value)}/></Field>
          </div>
          <Field label="Reason for shift">
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. delayed due to rain, client pushed back"/>
          </Field>
          <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
            <input type="checkbox" checked={setDelayed} onChange={e => setSetDelayed(e.target.checked)}/>
            Mark booking as "delayed"
          </label>
        </div>
        {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" icon={Send} onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Rescheduling…" : "Reschedule"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StatusReasonModal({
  booking, newStatus, onClose, onDone,
}: {
  booking: Booking; newStatus: BookingStatus; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema("app").rpc("set_project_booking_status", {
        p_booking_id: booking.booking_id,
        p_new_status: newStatus,
        p_reason:     reason || null,
      });
      if (error) throw error;
    },
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? "Status change failed"),
  });

  const titleByStatus: Record<BookingStatus, string> = {
    tentative: "Mark tentative", confirmed: "Confirm booking",
    in_progress: "Mark in progress", completed: "Mark completed",
    delayed: "Mark delayed", cancelled: "Cancel booking",
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-raised border border-line rounded-lg p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold">{titleByStatus[newStatus]}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={16}/></button>
        </div>
        <Field label="Reason (optional)">
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. crew arrived early, rain delay" autoFocus/>
        </Field>
        {error && <div className="mt-2 text-[12px] text-danger">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" icon={Send} onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Saving…" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}
